import { useEffect, useState } from 'react'

/**
 * The project logos of the token list (`/api/tokens/list`, approved info and basic pages), by lowercase address. ONE shared request per
 * five minutes for the whole page; a failed read is an empty map and is retried on the next use. Lets a logo that was uploaded on a token
 * page show wherever a bare address is drawn (lock tools, pools), not only on the token pages themselves.
 */
const KEEP_MS = 5 * 60_000
let map: Record<string, string> = {}
let at = 0
let pending: Promise<void> | null = null

function load(): Promise<void> {
  if (pending) return pending
  pending = fetch('/api/tokens/list')
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      const next: Record<string, string> = {}
      for (const i of Array.isArray(j?.items) ? j.items : []) {
        if (typeof i?.address === 'string' && typeof i?.logoUrl === 'string' && i.logoUrl.startsWith('https://')) next[i.address.toLowerCase()] = i.logoUrl
      }
      if (Object.keys(next).length) {
        map = next
        at = Date.now()
      }
    })
    .catch(() => undefined)
    .finally(() => {
      pending = null
    })
  return pending
}

export function useListLogo(address?: string | null): string | null {
  const key = address ? address.toLowerCase() : ''
  const [, bump] = useState(0)
  useEffect(() => {
    if (!key || typeof fetch !== 'function' || (at > 0 && Date.now() - at < KEEP_MS)) return
    let live = true
    load().then(() => live && bump((n) => n + 1))
    return () => {
      live = false
    }
  }, [key])
  return key ? map[key] ?? null : null
}
